/* Vereinslogos vor dem Hochladen verkleinern — die Entscheidungen.
 *
 * Reine Logik, kein DOM. Den Teil mit Canvas und Dekodieren (braucht einen Browser) macht
 * composables/shrinkLogo.js; er reicht seine Browser-Bausteine hier herein (`io`), damit
 * dieselbe Logik in Node mit Stellvertretern prüfbar ist.
 *
 * Regeln:
 *  - längere Seite höchstens 256 px, Seitenverhältnis bleibt, NIE vergrößern
 *  - Transparenz bleibt: die Zeichenfläche wird nicht gefüllt; Ausgabe WebP (mit Alpha) oder
 *    PNG, nie JPEG (domain/photoVariants.js encodeVariant)
 *  - ein Bild, das schon klein genug und PNG oder WebP ist, wird unverändert hochgeladen
 *    (kein überflüssiges Neukodieren); ein kleines JPEG wird in der gleichen Größe als
 *    WebP/PNG neu geschrieben, damit nie ein JPEG als Logo liegt
 *  - SVG bleibt, wie es ist (Vektor, nichts zu verkleinern)
 *  - lässt sich das Bild nicht lesen, wird das Original hochgeladen: ein exotisches Format
 *    soll das Hochladen nicht blockieren
 * Bereits hochgeladene Logos werden nicht angefasst.
 */

import { encodeVariant } from './photoVariants.js';

export const LOGO_MAX_SIDE = 256;
/* WebP-Qualität; bei Logos mit Text und Kanten bewusst hoch */
export const LOGO_QUALITY = 0.92;

const KEEP_TYPES = ['image/png', 'image/webp'];

/* Grenzen für Register-Logos (Entscheidung Bastian, 2026-10-07, N5): höchstens 1 MB je hochgeladener Datei,
   SVG höchstens 100 KB. Die Verkleinerung auf 256 px bleibt davor. Die Eingabegrenze schützt nur das
   Dekodieren im Browser vor riesigen Dateien. */
export const LOGO_MAX_BYTES = 1024 * 1024;
export const LOGO_MAX_SVG_BYTES = 100 * 1024;
export const LOGO_MAX_INPUT_BYTES = 20 * 1024 * 1024;

const isSvg = (file) => String(file && file.type).toLowerCase() === 'image/svg+xml';

/**
 * Prüfung VOR dem Verkleinern: Ist es überhaupt ein Bild, ist ein SVG klein genug, ist die Datei nicht absurd groß?
 * @returns {null | 'logoNotImage' | 'logoSvgTooLarge' | 'logoTooLarge'}  Schlüssel unter errors.*
 */
export function logoInputProblem(file) {
  if (!file || !/^image\//i.test(String(file.type))) return 'logoNotImage';
  if (isSvg(file)) return file.size > LOGO_MAX_SVG_BYTES ? 'logoSvgTooLarge' : null;
  return file.size > LOGO_MAX_INPUT_BYTES ? 'logoTooLarge' : null;
}

/**
 * Prüfung NACH dem Verkleinern, unmittelbar vor dem Hochladen: Die Datei, die wirklich hochgeladen wird,
 * ist höchstens 1 MB (SVG höchstens 100 KB).
 * @returns {null | 'logoSvgTooLarge' | 'logoTooLarge'}
 */
export function logoOutputProblem(file) {
  if (isSvg(file)) return file.size > LOGO_MAX_SVG_BYTES ? 'logoSvgTooLarge' : null;
  return file.size > LOGO_MAX_BYTES ? 'logoTooLarge' : null;
}

/**
 * Zielgröße: längere Seite höchstens `max`, Seitenverhältnis bleibt, nie größer als das Original.
 * Beide Seiten mindestens 1 px (ein extrem schmales Bild verschwindet nicht).
 * @returns {{width:number, height:number, scaled:boolean}}
 */
export function logoTargetSize(width, height, max = LOGO_MAX_SIDE) {
  const w = Math.round(Number(width));
  const h = Math.round(Number(height));
  if (!(w > 0) || !(h > 0)) return { width: w || 0, height: h || 0, scaled: false };
  const longest = Math.max(w, h);
  if (longest <= max) return { width: w, height: h, scaled: false };
  const k = max / longest;
  return {
    width: Math.max(1, Math.round(w * k)),
    height: Math.max(1, Math.round(h * k)),
    scaled: true,
  };
}

/**
 * Was passiert mit diesem Bild?
 * @returns {{action:'keep'} | {action:'encode', width:number, height:number}}
 */
export function planLogo({ width, height, type }, max = LOGO_MAX_SIDE) {
  if (String(type).toLowerCase() === 'image/svg+xml') return { action: 'keep' };
  if (!(width > 0) || !(height > 0)) return { action: 'keep' };
  const target = logoTargetSize(width, height, max);
  if (!target.scaled && KEEP_TYPES.includes(String(type).toLowerCase())) return { action: 'keep' };
  return { action: 'encode', width: target.width, height: target.height };
}

/** Dateiname mit der Endung des tatsächlichen Formats (Name ohne alte Endung). */
export function logoFileName(name, ext) {
  const base = String(name || 'logo').replace(/\.[^./\\]+$/, '') || 'logo';
  return `${base}.${ext}`;
}

/**
 * Datei für den Upload vorbereiten.
 * io = {
 *   decode(file) -> Promise<{ source, width, height, close() }>   (Browser: createImageBitmap / <img>)
 *   draw(decoded, width, height) -> canvas   (neue, durchsichtige Zeichenfläche, Bild eingepasst)
 *   toBlob(canvas, type, quality) -> Promise<Blob|null>
 *   makeFile(blob, name, type) -> File
 * }
 * Gibt die Eingabe unverändert zurück ('keep', Fehler) oder eine neue Datei.
 */
export async function shrinkLogoWith(file, io, max = LOGO_MAX_SIDE) {
  if (!file) return file;
  if (String(file.type).toLowerCase() === 'image/svg+xml') return file;

  let decoded;
  try {
    decoded = await io.decode(file);
  } catch (e) {
    console.warn('[logo] Bild nicht lesbar, Original wird hochgeladen:', e && e.message);
    return file;
  }

  try {
    const plan = planLogo({ width: decoded.width, height: decoded.height, type: file.type }, max);
    if (plan.action === 'keep') return file;

    const canvas = io.draw(decoded, plan.width, plan.height);
    const out = await encodeVariant((type, quality) => io.toBlob(canvas, type, quality), LOGO_QUALITY);
    return io.makeFile(out.blob, logoFileName(file.name, out.ext), out.contentType);
  } catch (e) {
    console.warn('[logo] Verkleinern fehlgeschlagen, Original wird hochgeladen:', e && e.message);
    return file;
  } finally {
    try { decoded.close(); } catch { /* nichts zu schließen */ }
  }
}
